import { api } from './api';

/**
 * Direct-to-storage uploads.
 *
 * The shape is fixed by the backend and is the same for every media type
 * (HANDOFF §4.1): ask the API to sign a URL, PUT the bytes straight to object
 * storage, then tell the API it landed. The bytes never pass through PHP.
 *
 * Written generically because the track upload in Phase 2 is this function with
 * a different pair of endpoints and a much larger file. The interesting
 * differences there - multipart for anything over ~100MB, and a 20-minute WAV
 * being ~212MB - are noted at `uploadDirect` below.
 */

export interface PresignedTarget {
  url: string;
  key: string;
  headers: Record<string, string>;
  expires_in: number;
}

export interface UploadOptions {
  /** 0-100. Called often enough to drive a progress bar smoothly. */
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * PUT a file to a presigned URL, reporting progress.
 *
 * `XMLHttpRequest` rather than `fetch`, and this is the one place in the client
 * where that is the right call: `fetch` still has no upload progress event in
 * any shipping browser. The streaming-request proposal that would fix it
 * requires HTTP/2 and is not universally available.
 *
 * For an avatar the difference is cosmetic. For a 200MB WAV on a domestic
 * connection it is the difference between a progress bar and a page that
 * appears to have frozen for four minutes, so the whole flow is built on the
 * API that can report it.
 */
export function uploadDirect(target: PresignedTarget, file: File, options: UploadOptions = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('PUT', target.url, true);

    for (const [name, value] of Object.entries(target.headers)) {
      /*
       * Sent verbatim. `Content-Type` is bound into the signature server-side,
       * so a different value here - including one a helpful library adds on its
       * own - invalidates the signature and object storage rejects the PUT with
       * a message about the signature rather than about the header.
       */
      request.setRequestHeader(name, value);
    }

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        options.onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();

        return;
      }

      reject(new Error(`Upload failed (${request.status}).`));
    };

    /*
     * A network-level failure, which for a cross-origin PUT is very often a
     * CORS rejection rather than a dead connection - and the browser
     * deliberately tells script nothing about which (§4.1a calls this "an error
     * that tells you nothing").
     *
     * So the message names the likely cause rather than pretending to know.
     */
    request.onerror = () =>
      reject(new Error('Could not reach storage. This is usually a CORS or network problem.'));

    request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

    options.signal?.addEventListener('abort', () => request.abort(), { once: true });

    request.send(file);
  });
}

/**
 * The whole avatar round trip: sign, upload, confirm.
 *
 * Returns once the API has accepted the upload - **not** once the image is
 * ready. Processing happens on the worker, so the caller has to wait for the
 * result separately. `pollUntil` is the interim mechanism; Phase 4 replaces it
 * with a realtime event on `private-user.{id}` (§8.1), which is the same
 * arrangement the track pipeline will use for `track.ready`.
 */
export async function uploadAvatar(file: File, options: UploadOptions = {}): Promise<void> {
  await uploadImage('/me/avatar', file, options);
}

/**
 * The profile header.
 *
 * The same round trip as an avatar at a different endpoint - see
 * `ProcessBanner` for why the two are separate jobs rather than one with a
 * shape argument.
 */
export async function uploadBanner(file: File, options: UploadOptions = {}): Promise<void> {
  await uploadImage('/me/banner', file, options);
}

/**
 * Cover art, for a track or an album.
 *
 * The same round trip as an avatar, at a different endpoint - and the server
 * routes all three through one `ImageUploads` for the same reason: the prefix
 * check that confines an upload to its uploader should exist once, not once per
 * image type.
 */
export async function uploadArtwork(
  subject: 'tracks' | 'collections',
  subjectId: number,
  file: File,
  options: UploadOptions = {},
): Promise<void> {
  await uploadImage(`/${subject}/${subjectId}/artwork`, file, options);
}

/**
 * Sign, PUT, confirm - for any endpoint pair that follows the `init`/`complete`
 * convention.
 *
 * Returns once the API has accepted the upload, **not** once the image is
 * ready: processing happens on the worker, so the caller waits separately.
 * `pollUntil` is the interim mechanism; Phase 4 replaces it with a realtime
 * event on `private-user.{id}` (§8.1).
 */
async function uploadImage(base: string, file: File, options: UploadOptions): Promise<void> {
  const target = await api.post<PresignedTarget>(`${base}/init`, {
    content_type: file.type,
    size_bytes: file.size,
  });

  await uploadDirect(target, file, options);

  await api.post(`${base}/complete`, { key: target.key });
}

/**
 * Poll until a condition holds, or give up.
 *
 * Deliberately not a general-purpose retry. It exists for one shape: work has
 * been queued, and the client needs to know when the result appears. Polling is
 * the honest interim answer - the alternative is a socket, and that is Phase 4.
 *
 * Returns false on timeout rather than throwing, because a slow worker is not
 * an error the user needs to see as a failure; the upload did land, and the
 * caller can say "still processing" instead.
 */
export async function pollUntil<T>(
  fetcher: () => Promise<T>,
  done: (value: T) => boolean,
  { intervalMs = 1500, timeoutMs = 60_000 }: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      if (done(await fetcher())) {
        return true;
      }
    } catch {
      // A transient failure mid-poll is not a reason to stop waiting for work
      // that was accepted; the next tick tries again.
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return false;
}

/* -------------------------------------------------------------------------- */
/* Tracks                                                                      */
/* -------------------------------------------------------------------------- */

export interface SinglePartInit extends PresignedTarget {
  track_id: number;
  mode: 'single';
}

export interface MultipartInit {
  track_id: number;
  mode: 'multipart';
  upload_id: string;
  key: string;
  part_bytes: number;
  parts: { number: number; url: string }[];
}

export type TrackInit = SinglePartInit | MultipartInit;

export interface TrackDetails {
  title: string;
  content_type: string;
  size_bytes: number;
  genre_id?: number | null;
  description?: string | null;
  visibility?: 'public' | 'unlisted' | 'private';
  /** The artist's own declaration. Absent means no - see the migration. */
  explicit?: boolean;
}

/**
 * PUT one part and hand back its ETag.
 *
 * The ETag is the point. S3-compatible storage will only assemble a multipart
 * upload if it is given every part's number and ETag, and the only place that
 * value exists is the response header of the PUT that stored it - it is not
 * derivable from the bytes (for multipart it is not even an MD5).
 *
 * Which means the bucket has to expose the header to script via CORS
 * (`ExposeHeaders: ["ETag"]`). Without it the PUT succeeds, `getResponseHeader`
 * returns null, and the upload fails at the *last* step after every byte has
 * been sent. That is why it is checked here rather than assumed.
 */
function uploadPart(
  url: string,
  blob: Blob,
  onBytes: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('PUT', url, true);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onBytes(event.loaded);
    };

    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(`Part upload failed (${request.status}).`));

        return;
      }

      const etag = request.getResponseHeader('ETag');

      if (!etag) {
        reject(
          new Error(
            'Storage did not return an ETag. The bucket needs ExposeHeaders: ["ETag"] in its CORS rules.',
          ),
        );

        return;
      }

      // Counted as fully sent: `upload.onprogress` is not guaranteed to fire a
      // final event at 100%, and without this the bar can stall at 97%.
      onBytes(blob.size);
      resolve(etag);
    };

    request.onerror = () =>
      reject(new Error('Could not reach storage. This is usually a CORS or network problem.'));
    request.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));

    signal?.addEventListener('abort', () => request.abort(), { once: true });

    request.send(blob);
  });
}

/** How many parts are in flight at once. */
const PART_CONCURRENCY = 3;

/**
 * Upload the parts of a multipart upload, a few at a time.
 *
 * Three rather than one because a single connection rarely saturates an upstream
 * link, and rarely rather than all of them because a 500MB file is 32 parts and
 * 32 concurrent PUTs is how you starve every other request the page needs -
 * including the token refresh that keeps the session alive mid-upload.
 *
 * Progress is aggregated across parts, so the bar reflects the file rather than
 * whichever part happens to be furthest along.
 */
async function uploadParts(
  init: MultipartInit,
  file: File,
  options: UploadOptions,
): Promise<{ number: number; etag: string }[]> {
  const loaded = new Map<number, number>();
  const results: { number: number; etag: string }[] = [];
  let next = 0;

  const report = () => {
    let total = 0;
    for (const bytes of loaded.values()) total += bytes;
    options.onProgress?.(Math.min(100, Math.round((total / file.size) * 100)));
  };

  const worker = async (): Promise<void> => {
    while (next < init.parts.length) {
      if (options.signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

      const part = init.parts[next++];

      // `noUncheckedIndexedAccess` - the bound was checked a line ago, but the
      // compiler cannot see that across the increment.
      if (!part) break;

      const start = (part.number - 1) * init.part_bytes;
      // `File.slice` does not read anything - it returns a view. The bytes are
      // pulled from disk as the request streams them, so a 500MB file never
      // sits in memory in one piece.
      const blob = file.slice(start, Math.min(start + init.part_bytes, file.size));

      const etag = await uploadPart(
        part.url,
        blob,
        (bytes) => {
          loaded.set(part.number, bytes);
          report();
        },
        options.signal,
      );

      results.push({ number: part.number, etag });
    }
  };

  await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, init.parts.length) }, worker));

  // Sorted because the workers finish out of order and the completion call is
  // specified as an ordered list.
  return results.sort((a, b) => a.number - b.number);
}

/**
 * The whole track round trip: init, upload, complete.
 *
 * Resolves once the API has accepted the upload and queued the transcode - the
 * track is `processing` at that point, not playable. The caller polls
 * `GET /tracks/{id}` for `ready`.
 *
 * Both modes are handled here rather than by the caller because which one
 * applies is a property of the file size that the server decides, and a UI that
 * has to branch on it would be a UI that can get it wrong.
 */
export async function uploadTrack(
  file: File,
  details: TrackDetails,
  options: UploadOptions = {},
): Promise<number> {
  const init = await api.post<TrackInit>('/tracks/init', details);

  if (init.mode === 'single') {
    await uploadDirect(init, file, options);
    await api.post(`/tracks/${init.track_id}/complete`, {});

    return init.track_id;
  }

  try {
    const parts = await uploadParts(init, file, options);

    await api.post(`/tracks/${init.track_id}/complete`, { upload_id: init.upload_id, parts });
  } catch (error) {
    /*
     * Tell storage to discard what was uploaded.
     *
     * Abandoned multipart uploads are not free - the parts occupy the bucket and
     * are billed until a lifecycle rule reaps them. Best-effort, because if the
     * abort itself fails there is nothing useful the user can do about it, and
     * the reconciliation sweep (§4.1) cleans up whatever the client could not.
     */
    await api.post(`/tracks/${init.track_id}/abort`, { upload_id: init.upload_id }).catch(() => {});

    throw error;
  }

  return init.track_id;
}
